/* =====================================================
   afk-audio — drumbeam theme JS
   Vanilla, no dependencies. Everything is progressive
   enhancement on top of server-rendered Liquid.
   ===================================================== */
(function () {
  'use strict';

  var SETTINGS = window.AFK || {};
  var LED_PALETTE = ['#FF2D2D', '#FF7A00', '#FFB800', '#00E676', '#00E5FF', '#2979FF', '#B14BFF', '#FF2E9A'];

  /* UI strings come from snippets/cart-drawer.liquid (translated); the fallback is the English copy */
  function str(key, fallback) {
    var ts = window.themeStrings || {};
    return ts[key] || fallback;
  }

  /* ---------- money formatting ---------- */
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    var format = SETTINGS.moneyFormat || '€{{amount}}';
    var parts = amount.split('.');
    var comma = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1];
    var dot = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];
    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, comma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, parts[0])
      .replace(/\{\{\s*amount\s*\}\}/g, dot)
      .replace(/[.,]00(?!\d)/, '');
  }

  /* ---------- theme toggle ---------- */
  function initThemeToggle() {
    // both toggles (header + menu) say whether the dark theme is on
    function paint() {
      var dark = document.documentElement.dataset.theme === 'dark';
      document.querySelectorAll('[data-theme-toggle]').forEach(function (b) { b.setAttribute('aria-pressed', dark ? 'true' : 'false'); });
    }
    document.querySelectorAll('[data-theme-toggle]').forEach(function (btn) {
      if (btn.dataset.toggleInit) return;  // re-run on every editor section load: bind each button once
      btn.dataset.toggleInit = '1';
      btn.addEventListener('click', function () {
        var next = document.documentElement.dataset.theme === 'light' ? 'dark' : 'light';
        document.documentElement.dataset.theme = next;
        try { localStorage.setItem('afk-theme', next); } catch (e) { /* private mode */ }
        paint();
      });
    });
    paint();
  }

  /* ---------- header scroll state ---------- */
  function initHeader() {
    var header = document.querySelector('.site-header');
    if (!header) return;
    var onScroll = function () {
      header.classList.toggle('is-scrolled', window.scrollY > 20);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- announcement bar: keep the fixed header docked right below it ---------- */
  function initAnnounce() {
    var bar = document.querySelector('.announce');
    if (!bar) { document.documentElement.style.setProperty('--announce-h', '0px'); return; }
    var setH = function () {
      document.documentElement.style.setProperty('--announce-h', bar.offsetHeight + 'px');
    };
    setH();
    window.addEventListener('resize', setH, { passive: true });
    window.addEventListener('load', setH);
    if (window.ResizeObserver) { new ResizeObserver(setH).observe(bar); }
  }

  /* ---------- overlays (mobile menu + cart drawer) ----------
     Both behave as modal dialogs: everything outside the open one is inert, focus moves in
     and comes back on close, Tab stays inside, and Esc or the browser's Back button closes
     them. Back works through one extra history entry per open overlay (same URL). */
  var menuApi = null;     // set by initMobileMenu
  var inertBg = [];       // nodes we made inert for the open overlay
  var lastTap = null;     // last clicked control: focus return target when focus got lost on the way
  var backPending = false, afterPop = null, pendingPush = null, popSeq = 0;

  function skipInert(el) {
    var tag = el.tagName;
    if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'LINK' || tag === 'TEMPLATE' || tag === 'NOSCRIPT') return true;
    if (/^shopify-pc/.test(el.id || '')) return true;  // Shopify cookie consent: never touched
    var role = el.getAttribute('role');
    return role === 'dialog' || role === 'alertdialog';  // other Shopify-injected dialogs
  }

  function setBackgroundInert(overlay) {
    inertBg.forEach(function (el) { el.removeAttribute('inert'); });
    inertBg = [];
    if (!overlay) return;
    for (var node = overlay; node && node.parentElement && node !== document.body; node = node.parentElement) {
      var sibs = node.parentElement.children;
      for (var i = 0; i < sibs.length; i++) {
        var s = sibs[i];
        if (s === node || s.hasAttribute('inert') || skipInert(s)) continue;
        s.setAttribute('inert', '');
        inertBg.push(s);
      }
    }
  }

  function focusables(root) {
    var sel = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';
    return [].filter.call(root.querySelectorAll(sel), function (el) {
      return el.getClientRects().length > 0 && !el.closest('[inert]');
    });
  }

  /* Tab wraps inside the panel. "At the end" means nothing focusable follows in document order,
     which also holds when focus sits inside a wallet button's shadow root (activeElement = its host).
     Backwards, the wrap parks focus on an empty end marker and lets the browser take the last step:
     the last control can be a wallet button in a closed shadow root, which .focus() can't reach. */
  function trapTab(panel, e) {
    var list = focusables(panel);
    if (!list.length) return;
    var a = document.activeElement;
    var inside = panel.contains(a);
    if (!inside && a && a !== document.body) return;  // e.g. a wallet's own modal: leave its keyboard alone
    var rel = function (el, bit) { return el !== a && !a.contains(el) && (a.compareDocumentPosition(el) & bit); };
    if (e.shiftKey) {
      if (!inside || !list.some(function (el) { return rel(el, 2); })) focusSafely(focusEnd(panel));  // no preventDefault
    } else if (!inside || !list.some(function (el) { return rel(el, 4); })) {
      e.preventDefault();
      focusSafely(list[0]);
    }
  }
  function focusEnd(panel) {
    var end = panel.querySelector('[data-focus-end]');
    if (!end) {
      end = document.createElement('span');
      end.setAttribute('tabindex', '-1');
      end.setAttribute('data-focus-end', '');
      end.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;overflow:hidden;outline:none;pointer-events:none';
      panel.appendChild(end);
    }
    return end;
  }

  function focusSafely(el) {
    if (!el || !el.focus || !document.documentElement.contains(el)) return;
    try { el.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
  }

  function historyOk() { return !(window.Shopify && window.Shopify.designMode) && window.history && history.pushState; }
  function overlayEntry() { return !!(history.state && history.state.afkOverlay); }

  /* Coming back from our overlay entry must not move the page: after an in-page anchor (#specs, #demo)
     the browser would otherwise restore that entry's scroll position and jump to the anchor.
     'manual' is set on the page entry right before the push and put back to 'auto' once we are back on it. */
  function scrollHold() { try { if ('scrollRestoration' in history) history.scrollRestoration = 'manual'; } catch (e) { /* ignore */ } }
  function scrollRelease() {
    setTimeout(function () {
      if (overlayEntry()) return;
      try { if ('scrollRestoration' in history) history.scrollRestoration = 'auto'; } catch (e) { /* ignore */ }
    }, 0);
  }

  function pushOverlay(name) {
    if (!historyOk()) return;
    if (backPending) { pendingPush = name; return; }
    try {
      if (overlayEntry()) history.replaceState({ afkOverlay: name }, '');
      else { scrollHold(); history.pushState({ afkOverlay: name }, ''); }
    } catch (e) { /* ignore */ }
  }

  /* drop our history entry (closing by X / Esc / overlay); `after` runs once we're back on the page's own entry */
  function popOverlay(after) {
    if (!historyOk() || !overlayEntry() || backPending) { if (after) after(); return; }
    backPending = true;
    afterPop = after || null;
    history.back();
    var seq = ++popSeq;
    setTimeout(function () { if (backPending && seq === popSeq) { backPending = false; runAfterPop(); } }, 700);  // safety net
  }

  function runAfterPop() {
    var fn = afterPop;
    afterPop = null;
    if (pendingPush) {
      var n = pendingPush;
      pendingPush = null;
      if (drawerIsOpen() || (menuApi && menuApi.isOpen())) try { scrollHold(); history.pushState({ afkOverlay: n }, ''); } catch (e) { /* ignore */ }
    }
    scrollRelease();
    if (fn) fn();
  }

  function anyOverlayOpen() { return drawerIsOpen() || !!(menuApi && menuApi.isOpen()); }

  /* leaving the page from an open overlay: close it, drop our entry first, then go —
     so Back from the next page lands on this page instead of on a stale overlay entry */
  function leaveOverlay(go) {
    if (drawerIsOpen()) closeDrawer({ fromPop: true, keepFocus: true });
    if (menuApi && menuApi.isOpen()) menuApi.close({ fromPop: true, keepFocus: true });
    popOverlay(go);
  }

  function bindLeave(root) {
    root.addEventListener('click', function (e) {
      var a = e.target.closest('a[href]');
      if (!a || e.defaultPrevented || !anyOverlayOpen() || !overlayEntry()) return;
      if (a.hasAttribute('data-quick-add') || a.target === '_blank' || a.hasAttribute('download')) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (a.protocol !== 'http:' && a.protocol !== 'https:') return;
      var href = a.href;
      e.preventDefault();
      leaveOverlay(function () { window.location.href = href; });
    });
    root.addEventListener('submit', function (e) {
      var form = e.target;
      if (e.defaultPrevented || form.hasAttribute('data-ajax-cart') || !anyOverlayOpen() || !overlayEntry()) return;
      var sub = e.submitter;
      // the way to checkout goes out at once: no history detour on the money path (Back may land on the open drawer once).
      // Without submitter info (older WebViews) the drawer's native submit is left alone too, so the browser still sends the button's name.
      if (sub ? sub.name === 'checkout' : root.id === 'CartDrawer') return;
      e.preventDefault();
      leaveOverlay(function () {
        if (sub && sub.name) {
          var h = document.createElement('input');
          h.type = 'hidden'; h.name = sub.name; h.value = sub.value || '';
          form.appendChild(h);
        }
        form.submit();
      });
    });
  }

  function initOverlays() {
    // a reload (or a non-bfcache Back) onto one of our entries: the overlay is closed, so the entry is just a page entry now
    if (historyOk() && overlayEntry()) try { history.replaceState(null, ''); } catch (e) { /* ignore */ }
    document.addEventListener('click', function (e) {
      var c = e.target.closest && e.target.closest('a, button, input, select, summary, [tabindex]');
      if (c) lastTap = c;
    }, true);
    document.addEventListener('keydown', function (e) {
      var drawerOpen = drawerIsOpen(), menuOpen = !!(menuApi && menuApi.isOpen());
      if (!drawerOpen && !menuOpen) return;
      if (e.key === 'Escape') {
        if (drawerOpen) closeDrawer(); else menuApi.close();
      } else if (e.key === 'Tab') {
        trapTab(drawerOpen ? cartDrawer.querySelector('.cart-drawer__panel') : menuApi.panel, e);
      }
    });
    window.addEventListener('popstate', function () {
      if (backPending) { backPending = false; runAfterPop(); return; }  // our own history.back()
      if (drawerIsOpen()) closeDrawer({ fromPop: true });
      if (menuApi && menuApi.isOpen()) menuApi.close({ fromPop: true });
      scrollRelease();
    });
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      // restored from the back/forward cache: close what was open, drop a stale overlay entry, re-sync the cart
      if (drawerIsOpen()) closeDrawer({ fromPop: true, keepFocus: true });
      if (menuApi && menuApi.isOpen()) menuApi.close({ fromPop: true, keepFocus: true });
      if (overlayEntry()) popOverlay();
      fetchCart().then(function (cart) { updateCartUi(cart); refreshWallets(); }).catch(function () { /* keep what is shown */ });
    });
  }

  /* ---------- mobile menu ---------- */
  function initMobileMenu() {
    var menu = document.getElementById('MobileMenu');
    if (!menu) return;
    var sheet = menu.querySelector('.mobile-menu__sheet') || menu;
    var openers = document.querySelectorAll('[data-menu-open]');
    var lastFocus = null;
    // header.liquid renders the static markup; the dialog semantics are added here
    menu.setAttribute('inert', '');
    menu.setAttribute('aria-hidden', 'true');
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    if (openers[0] && openers[0].getAttribute('aria-label')) sheet.setAttribute('aria-label', openers[0].getAttribute('aria-label'));
    openers.forEach(function (b) { b.setAttribute('aria-controls', 'MobileMenu'); b.setAttribute('aria-expanded', 'false'); });

    var isOpen = function () { return menu.classList.contains('is-open'); };
    var open = function () {
      if (isOpen()) return;
      if (drawerIsOpen()) closeDrawer({ fromPop: true, keepFocus: true });
      lastFocus = document.activeElement;
      menu.classList.add('is-open');
      menu.removeAttribute('inert');
      menu.setAttribute('aria-hidden', 'false');
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      document.body.style.overflow = 'hidden';
      setBackgroundInert(menu);
      focusSafely(menu.querySelector('[data-menu-close]'));
      pushOverlay('menu');
    };
    var close = function (opts) {
      opts = opts || {};
      if (!isOpen()) return;
      menu.classList.remove('is-open');
      menu.setAttribute('inert', '');
      menu.setAttribute('aria-hidden', 'true');
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      document.body.style.overflow = '';
      setBackgroundInert(null);
      if (!opts.keepFocus) focusSafely(lastFocus);
      if (!opts.fromPop) popOverlay();
    };
    menuApi = { el: menu, panel: sheet, isOpen: isOpen, open: open, close: close, returnTarget: function () { return lastFocus; } };

    openers.forEach(function (b) { b.addEventListener('click', open); });
    document.querySelectorAll('[data-menu-close]').forEach(function (b) { b.addEventListener('click', function () { close(); }); });
    menu.addEventListener('click', function (e) { if (e.target === menu) close(); });
    bindLeave(menu);
  }

  /* ---------- LED bar renderer ---------- */
  function renderLedBar(el, zones, activeIndex, onClick) {
    var row = el.querySelector('.led-bar__row') || el.appendChild(Object.assign(document.createElement('div'), { className: 'led-bar__row' }));
    row.innerHTML = '';
    zones.forEach(function (z, zi) {
      for (var i = 0; i < z.n; i++) {
        var led = document.createElement('span');
        led.className = 'led-bar__led' + (zi === activeIndex ? ' is-active' : '');
        led.style.background = z.c;
        led.style.boxShadow = zi === activeIndex
          ? '0 0 10px ' + z.c + ', 0 0 22px ' + z.c
          : '0 0 4px ' + z.c;
        if (onClick) {
          (function (idx) { led.addEventListener('click', function () { onClick(idx); }); })(zi);
          el.classList.add('led-bar--clickable');
        }
        row.appendChild(led);
      }
      if (zi < zones.length - 1) {
        var gap = document.createElement('span');
        gap.className = 'led-bar__gap';
        row.appendChild(gap);
      }
    });
  }

  function initLedBars() {
    document.querySelectorAll('.led-bar[data-zones]').forEach(function (el) {
      var zones;
      try { zones = JSON.parse(el.dataset.zones); } catch (e) { return; }
      var height = parseInt(el.dataset.height || '18', 10);
      var active = parseInt(el.dataset.active || '-1', 10);
      renderLedBar(el, zones, active);
      var row = el.querySelector('.led-bar__row');
      if (row) row.style.height = height + 'px';
    });
  }

  /* ---------- live configurator ---------- */
  function initConfigurators() {
    document.querySelectorAll('[data-configurator]').forEach(function (root) {
      var bar = root.querySelector('.led-bar');
      var tagEl = root.querySelector('[data-config-preset]');
      if (!bar) return;

      // Each button is a saved mapping. Some split the bar into even zones,
      // others use custom widths — the point is the layout is yours to switch.
      function zones(widths) {
        return widths.map(function (w, i) { return { c: LED_PALETTE[i % LED_PALETTE.length], n: w }; });
      }
      var presets = {
        1: { tag: str('configFullBar', 'full bar'), zones: zones([56]) },
        4: { tag: str('configCustomWidths', 'custom widths'), zones: zones([20, 10, 8, 18]) },
        8: { tag: str('configEvenSplit', 'even split'), zones: zones([7, 7, 7, 7, 7, 7, 7, 7]) },
        12: { tag: str('configCustomWidths', 'custom widths'), zones: zones([6, 6, 6, 6, 3, 3, 3, 3, 5, 5, 5, 5]) }
      };
      var n = 8;

      function paint() {
        var preset = presets[n];
        renderLedBar(bar, preset.zones, -1);
        var row = bar.querySelector('.led-bar__row');
        if (row) row.style.height = '20px';
        if (tagEl) tagEl.textContent = preset.tag;
        root.querySelectorAll('[data-config-zones]').forEach(function (btn) {
          var on = parseInt(btn.dataset.configZones, 10) === n;
          btn.classList.toggle('is-active', on);
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      }
      root.querySelectorAll('[data-config-zones]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          n = parseInt(btn.dataset.configZones, 10);
          paint();
        });
      });
      paint();
    });
  }

  /* ---------- mobile buy bar ---------- */
  function initMobileBuyBar() {
    var bar = document.querySelector('.mobile-buybar');
    if (!bar || !('IntersectionObserver' in window)) return;
    // visible whenever no primary buy button is really usable on screen, and never over the footer.
    // "Usable" = at least 60 % of it between the fixed header and the bar's own strip at the bottom,
    // so a button tucked under the header or behind the bar doesn't count as visible.
    var ctas = document.querySelectorAll('.hero__actions .btn-primary, .convert__actions .btn-primary, .buyrow [data-quick-add]');
    var footer = document.querySelector('.site-footer');
    var header = document.querySelector('.site-header');
    var top = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    var bottom = bar.offsetHeight || 76;
    var inView = new Set();
    // hidden = inert, so keyboard and screen-reader focus never land on the off-screen bar
    var update = function () { var on = inView.size === 0; bar.classList.toggle('is-visible', on); if (bar.inert !== !on) bar.inert = !on; };
    bar.inert = true;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting && en.intersectionRatio >= 0.6) inView.add(en.target); else inView.delete(en.target); });
      update();
    }, { rootMargin: '-' + top + 'px 0px -' + bottom + 'px 0px', threshold: [0, 0.6, 1] });
    ctas.forEach(function (t) { io.observe(t); });
    if (footer) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (en.isIntersecting) inView.add(en.target); else inView.delete(en.target); });
        update();
      }).observe(footer);
    }
  }

  /* ---------- bottom bars step aside while a text field has focus (phone keyboard) ---------- */
  function initTypingState() {
    var field = 'input:not([type="checkbox"]):not([type="radio"]):not([type="hidden"]):not([type="submit"]):not([type="button"]), textarea, select';
    document.addEventListener('focusin', function (e) {
      if (e.target.matches && e.target.matches(field)) document.body.classList.add('is-typing');
    });
    document.addEventListener('focusout', function () {
      setTimeout(function () {
        var a = document.activeElement;
        if (!a || !a.matches || !a.matches(field)) document.body.classList.remove('is-typing');
      }, 0);
    });
  }

  /* ---------- single-open FAQ groups ---------- */
  function initFaqGroups() {
    document.querySelectorAll('[data-faq-single]').forEach(function (group) {
      if (group.dataset.faqInit) return;  // re-run on editor section loads: bind once
      group.dataset.faqInit = '1';
      group.querySelectorAll('details').forEach(function (d) {
        d.addEventListener('toggle', function () {
          if (!d.open) return;
          var s = d.querySelector('summary');
          var y0 = s ? s.getBoundingClientRect().top : 0;
          group.querySelectorAll('details[open]').forEach(function (other) {
            if (other !== d) other.open = false;
          });
          // phones: an open answer above the tapped question just collapsed; scroll by the same amount
          // so the question stays where the finger was (desktop keeps the plain behaviour)
          if (s && phoneMq && phoneMq.matches) {
            var dy = s.getBoundingClientRect().top - y0;
            if (Math.abs(dy) > 1) window.scrollBy({ top: dy, behavior: 'instant' });
          }
        });
      });
    });
  }

  /* ---------- cart state ---------- */
  var cartDrawer = null;
  var cartState = null;
  var drawerReturn = null;
  var WALLETS = 'cart-wallets';  // sections/cart-wallets.liquid, rendered with every AJAX cart change
  var phoneMq = window.matchMedia ? window.matchMedia('(max-width: 880px)') : null;
  var walletRetry = 0, walletTimer = null;  // retries after the bundled wallet section came back empty (null): 2 per add
  var capped = new Map();        // drawer lines Shopify capped at the stock count → the quantity it let through: their '+' stays
                                 // off until '−', remove, or the line changes anywhere else (e.g. on /cart)

  /* locale root ('/' or '/de/'): section HTML is rendered in the shopper's language. The cart endpoints
     themselves stay on /cart/*.js as before (the theme dev proxy can't pass /de/cart/*.js). */
  function shopRoot() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }

  function fetchCart() {
    // an HTTP error (e.g. a 429 rate-limit page) rejects instead of passing a non-cart object on
    return fetch('/cart.js').then(function (r) {
      if (!r.ok) throw new Error('cart.js ' + r.status);
      return r.json();
    });
  }

  function cartCount() {
    if (cartState) return cartState.item_count;
    var el = document.querySelector('[data-cart-count]');
    return el ? parseInt(el.dataset.count || el.textContent, 10) || 0 : 0;
  }

  function updateCartUi(cart) {
    cartState = cart;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
      el.dataset.count = cart.item_count;
    });
    renderDrawer(cart);
    renderShippingBars(cart);
    document.dispatchEvent(new CustomEvent('afk:cart', { detail: cart }));
  }

  function renderShippingBars(cart) {
    var threshold = (SETTINGS.freeShippingThreshold || 0) * 100;
    document.querySelectorAll('[data-shipping-progress]').forEach(function (root) {
      var label = root.querySelector('.shipping-progress__label');
      var fill = root.querySelector('.shipping-progress__fill');
      if (!threshold || cart.item_count === 0) { root.style.display = 'none'; return; }
      root.style.display = '';
      var remaining = Math.max(0, threshold - cart.total_price);
      var unlocked = remaining === 0;
      root.classList.toggle('is-unlocked', unlocked);  // phones: /cart hides the bar then, the drawer drops its track
      if (label) {
        label.textContent = unlocked
          ? str('freeShippingUnlocked', 'free shipping unlocked')
          : str('freeShippingRemaining', '[amount] until free shipping').replace('[amount]', formatMoney(remaining));
        label.classList.toggle('is-unlocked', unlocked);
      }
      if (fill) {
        fill.style.width = Math.min(100, (cart.total_price / threshold) * 100) + '%';
        fill.classList.toggle('is-unlocked', unlocked);
      }
    });
  }

  function drawerLineHtml(item) {
    var img = item.image
      ? '<img src="' + item.image.replace(/(\.[a-z]+)(\?|$)/, '_160x$1$2') + '" alt="" loading="lazy">'
      : '';
    var variant = item.variant_title && item.variant_title !== 'Default Title'
      ? '<div class="fineprint cart-line__meta">' + item.variant_title + '</div>' : '';
    return '' +
      '<div class="cart-drawer__line" data-line-key="' + item.key + '">' +
      '  <div class="cart-line__img">' + img + '</div>' +
      '  <div class="cart-line__main">' +
      '    <div style="display:flex; justify-content:space-between; gap:12px;">' +
      '      <div><a class="cart-line__title" href="' + item.url + '">' + item.product_title + '</a>' + variant + '</div>' +
      '      <span class="cart-line__price">' + formatMoney(item.final_line_price) + '</span>' +
      '    </div>' +
      '    <div class="cart-line__controls">' +
      '      <div class="cart-qty">' +
      '        <button type="button" data-qty-change="-1" aria-label="' + str('qtyDecrease', 'decrease quantity') + '">−</button>' +
      '        <span>' + item.quantity + '</span>' +
      '        <button type="button" data-qty-change="1" aria-label="' + str('qtyIncrease', 'increase quantity') + '"' + (capped.has(item.key) ? ' aria-disabled="true"' : '') + '>+</button>' +  // aria-disabled: stays focusable
      '      </div>' +
      '      <button type="button" class="cart-remove" data-line-remove>' + str('cartRemove', 'remove') + '</button>' +
      '    </div>' +
      '  </div>' +
      '</div>';
  }

  function renderDrawer(cart) {
    cartDrawer = cartDrawer || document.getElementById('CartDrawer');
    if (!cartDrawer) return;
    var lines = cartDrawer.querySelector('[data-drawer-lines]');
    var foot = cartDrawer.querySelector('[data-drawer-foot]');
    var count = cartDrawer.querySelector('[data-drawer-count]');
    // a cap only holds while the line still has the quantity Shopify capped it at
    capped.forEach(function (q, k) {
      var it = cart.items.filter(function (i) { return i.key === k; })[0];
      if (!it || it.quantity !== q) capped.delete(k);
    });
    if (count) {
      var unit = cart.item_count === 1 ? str('cartUnitOne', 'item') : str('cartUnitOther', 'items');
      count.textContent = cart.item_count + ' ' + unit;
    }
    if (lines) {
      var act = document.activeElement, hadFocus = lines.contains(act), fKey = null, fSel = null;
      if (hadFocus) {
        var fl = act.closest('[data-line-key]');
        fKey = fl && fl.dataset.lineKey;
        fSel = act.getAttribute('data-qty-change') ? '[data-qty-change="' + act.getAttribute('data-qty-change') + '"]' : null;
      }
      if (cart.item_count === 0) {
        lines.innerHTML =
          '<div class="cart-drawer__empty">' +
          '<div class="cart-drawer__empty-title">' + str('cartEmpty', 'your cart is empty.') + '</div>' +
          '<div class="fineprint">' + str('cartEmptyHint', 'add drumbeam or pick up some accessories.') + '</div>' +
          (SETTINGS.mainVariantId ? '<button type="button" class="btn btn-primary" style="margin-top: 20px;" data-drawer-quick-add="' + SETTINGS.mainVariantId + '">' + str('orderMain', 'order drumbeam') + '</button>' : '') +
          // phones: the hint mentions accessories, so there is a way to them (desktop unchanged)
          '<div class="show-mobile" style="margin-top: 16px;"><a class="link-underline" href="' + str('accessoriesUrl', '/collections/accessories') + '">' + str('shopAccessories', 'shop accessories') + '</a></div>' +
          '</div>';
      } else {
        lines.innerHTML = cart.items.map(drawerLineHtml).join('');  // also drops any pending quantity state
      }
      // the control that had focus was rebuilt: put focus back on it (or on close) so it stays in the dialog
      if (hadFocus && drawerIsOpen()) {
        // a '+' that just got capped keeps focus (aria-disabled), so the next Enter doesn't land on '−'
        var again = fKey && fSel ? lines.querySelector('[data-line-key="' + fKey + '"] ' + fSel) : null;
        focusSafely(again || cartDrawer.querySelector('.cart-drawer__close'));
      }
    }
    if (foot) foot.style.display = cart.item_count === 0 ? 'none' : '';
    var subtotal = cartDrawer.querySelector('[data-drawer-subtotal]');
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
  }

  /* wallet row (Shop Pay, Apple Pay, Google Pay, PayPal) in the drawer. Phones only (hidden above 880px).
     - page load with items: Liquid parks the row in a <template>; it goes live when the drawer first opens,
       so a page view that never opens the drawer loads no wallet scripts (and desktop never does)
     - AJAX changes: Shopify renders sections/cart-wallets.liquid with the change; an unchanged row is left
       alone so the buttons don't re-initialise on every quantity tap */
  function walletHost() {
    cartDrawer = cartDrawer || document.getElementById('CartDrawer');
    return cartDrawer && cartDrawer.querySelector('[data-drawer-wallets]');
  }

  function activateWallets() {
    var host = walletHost();
    if (!host || (phoneMq && !phoneMq.matches)) return;
    [].forEach.call(host.querySelectorAll('template[data-wallets-later]'), function (tpl) {
      tpl.parentNode.replaceChild(document.importNode(tpl.content, true), tpl);
    });
  }

  /* phones: a cart with items but no wallet row (e.g. the section render came back empty) asks for it again */
  function ensureWallets() {
    var host = walletHost();
    if (!host || !cartState || !cartState.item_count || (phoneMq && !phoneMq.matches)) return;
    if (host.querySelector('[data-cart-wallets], template[data-wallets-later]')) return;
    refreshWallets();
  }

  function renderWallets(html) {
    var host = walletHost();
    if (!host) return;
    if (typeof html !== 'string') {
      // null = section failed to render: keep what is there, and try twice more if that is nothing
      if (walletRetry < 2 && !walletTimer) {
        walletRetry++;
        walletTimer = setTimeout(function () { walletTimer = null; ensureWallets(); }, walletRetry === 1 ? 800 : 1500);
      }
      return;
    }
    walletRetry = 0;
    var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-wallets]');
    var cur = host.querySelector('[data-cart-wallets]');
    if (!fresh) { host.innerHTML = ''; return; }  // cart is empty
    if (phoneMq && !phoneMq.matches) {  // desktop: keep it dormant
      if (!cur) host.innerHTML = '<template data-wallets-later>' + fresh.outerHTML + '</template>';
      return;
    }
    var keep = cur && cur.querySelector('shopify-accelerated-checkout-cart');
    var next = fresh.querySelector('shopify-accelerated-checkout-cart');
    if (keep && next && keep.getAttribute('wallet-configs') === next.getAttribute('wallet-configs')) return;
    host.innerHTML = fresh.outerHTML;
  }

  function refreshWallets() {
    if (!cartDrawer) return Promise.resolve();
    return fetch(shopRoot() + '?sections=' + WALLETS).then(function (r) { return r.json(); }).then(function (s) {
      renderWallets(s && s[WALLETS]);
    }).catch(function () { /* keep the current row */ });
  }

  function drawerStatus(msg) {
    var st = cartDrawer && cartDrawer.querySelector('[data-drawer-status]');
    if (!st) return;
    st.textContent = msg || '';
    st.hidden = !msg;
  }

  function drawerIsOpen() { return !!(cartDrawer && cartDrawer.classList.contains('is-open')); }

  function openDrawer() {
    cartDrawer = cartDrawer || document.getElementById('CartDrawer');
    if (!cartDrawer || drawerIsOpen()) return;
    var a = document.activeElement;
    var ret = a && a !== document.body && !cartDrawer.contains(a) ? a : lastTap;
    if (menuApi && menuApi.isOpen()) menuApi.close({ fromPop: true, keepFocus: true });  // one overlay at a time; its history entry is reused
    if (ret && menuApi && menuApi.el.contains(ret)) ret = menuApi.returnTarget() || document.querySelector('[data-menu-open]');
    drawerReturn = ret;
    activateWallets();
    if (!walletTimer) ensureWallets();  // a retry is already on its way otherwise
    cartDrawer.classList.add('is-open');
    cartDrawer.removeAttribute('inert');
    cartDrawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    setBackgroundInert(cartDrawer);
    focusSafely(cartDrawer.querySelector('.cart-drawer__close'));
    pushOverlay('drawer');
  }
  function closeDrawer(opts) {
    opts = opts || {};
    if (!drawerIsOpen()) return;
    cartDrawer.classList.remove('is-open');
    cartDrawer.setAttribute('inert', '');
    cartDrawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    setBackgroundInert(null);
    drawerStatus('');
    if (!opts.keepFocus) focusSafely(drawerReturn);
    drawerReturn = null;
    if (!opts.fromPop) popOverlay();
  }

  function changeLine(key, quantity) {
    drawerStatus('');
    if (!walletTimer) walletRetry = 0;
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity, sections: WALLETS, sections_url: shopRoot() })
    }).then(function (r) {
      // an HTTP error carries Shopify's message; a rate-limit page may not be JSON
      if (!r.ok) return r.json().catch(function () { return {}; }).then(function (e) { e = e || {}; if (!e.status) e.status = r.status; throw e; });
      return r.json();
    }).then(function (cart) {
      updateCartUi(cart);
      renderWallets(cart.sections && cart.sections[WALLETS]);
    }).catch(function (err) {
      // show the real quantities (or, if the cart can't be read either, the last known ones) and say why.
      // More than in stock (422): the message sits under that line and its '+' stays off.
      var msg = (err && err.description) || str('cartUpdateError', 'could not update the cart.');
      // The cap is the quantity the fresh cart holds; without a fresh cart there is none (the next '+' just asks again).
      var cap = !!(err && err.status === 422 && quantity > 0);
      return fetchCart().then(function (cart) {
        var it = cap ? cart.items.filter(function (i) { return i.key === key; })[0] : null;
        if (it) capped.set(key, it.quantity);
        updateCartUi(cart);
      }, function () { if (cartState) renderDrawer(cartState); }).then(function () {
        var c = cap && cartDrawer && cartDrawer.querySelector('[data-line-key="' + key + '"] .cart-line__controls');
        if (c) inlineStatus(c, msg); else drawerStatus(msg);
      });
    });
  }

  function addItems(items) {
    var wallets;
    drawerStatus('');
    if (!walletTimer) walletRetry = 0;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items, sections: WALLETS, sections_url: shopRoot() })
    }).then(function (r) {
      if (!r.ok) return r.json().catch(function () { return {}; }).then(function (e) { throw e; });
      return r.json().then(function (res) {
        wallets = res && res.sections ? res.sections[WALLETS] : undefined;
        // the add went in: retry the cart read once, and if it still fails say so (addFailed must not report a failed add)
        return fetchCart().catch(function () {
          return new Promise(function (d) { setTimeout(d, 800); }).then(fetchCart);
        }).catch(function () {
          var e = new Error('added');
          e.added = items.reduce(function (n, it) { return n + (parseInt(it.quantity, 10) || 1); }, 0);
          throw e;
        });
      });
    }).then(function (cart) {
      updateCartUi(cart);
      renderWallets(wallets);
      // let the new lines lay out while the panel is still off-screen, then slide it in (no layout shift)
      return new Promise(function (done) {
        requestAnimationFrame(function () { requestAnimationFrame(function () { openDrawer(); done(); }); });
      });
    });
  }

  /* a failed add: no native alert. If part of it went in (e.g. capped at the stock level) the drawer
     shows the result with Shopify's message; otherwise the message sits right after the trigger. */
  function inlineStatus(trigger, msg) {
    if (!trigger || !trigger.parentNode) return;
    var st = trigger.nextElementSibling;
    if (!st || !st.hasAttribute('data-add-status')) {
      st = document.createElement('p');
      st.className = 'fineprint form-status form-status--error';
      st.setAttribute('role', 'status');
      st.setAttribute('data-add-status', '');
      trigger.parentNode.insertBefore(st, trigger.nextSibling);
    }
    st.textContent = msg;
  }
  function clearInlineStatus(trigger) {
    var st = trigger && trigger.nextElementSibling;
    if (st && st.hasAttribute('data-add-status')) st.parentNode.removeChild(st);
  }

  function addFailed(trigger, err, prevCount, fallbackHref) {
    if (err && err.added) {
      // added, but the cart could not be read back (e.g. rate-limited): count up, open the drawer, refresh shortly
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        var n = (parseInt(el.dataset.count || el.textContent, 10) || 0) + err.added;
        el.textContent = n;
        el.dataset.count = n;
      });
      openDrawer();
      drawerStatus(str('addedRefreshing', 'added. refreshing your cart…'));
      setTimeout(function () {
        fetchCart().then(function (cart) { updateCartUi(cart); drawerStatus(''); return refreshWallets(); }).catch(function () { /* keep the note */ });
      }, 1500);
      return Promise.resolve();
    }
    var msg = (err && err.description) || str('addError', 'could not add to cart.');
    var inline = function () {
      var go = function () {
        if (fallbackHref) window.location.href = fallbackHref;  // a link still leads to its page
        else inlineStatus(trigger, msg);
      };
      if (!anyOverlayOpen() && overlayEntry()) popOverlay(go); else go();  // e.g. the menu was closed for this add
    };
    return fetchCart().then(function (cart) {
      updateCartUi(cart);
      if (cart.item_count > prevCount) {
        return refreshWallets().then(function () {
          openDrawer();
          drawerStatus(msg);
        });
      } else {
        inline();
      }
    }, inline);
  }

  function initCart() {
    cartDrawer = document.getElementById('CartDrawer');
    document.querySelectorAll('[data-cart-open]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        if (!cartDrawer) return; // fall through to /cart page
        e.preventDefault();
        openDrawer();
      });
    });
    if (cartDrawer) {
      cartDrawer.querySelectorAll('[data-drawer-close]').forEach(function (b) { b.addEventListener('click', function () { closeDrawer(); }); });
      cartDrawer.querySelector('.cart-drawer__overlay').addEventListener('click', function () { closeDrawer(); });
      bindLeave(cartDrawer);
      cartDrawer.addEventListener('click', function (e) {
        var qa = e.target.closest('[data-drawer-quick-add]');
        if (qa) {
          if (qa.getAttribute('aria-busy') === 'true') return;
          var prev = cartCount();
          clearInlineStatus(qa);
          qa.setAttribute('aria-busy', 'true');
          addItems([{ id: parseInt(qa.dataset.drawerQuickAdd, 10), quantity: 1 }]).catch(function (err) {
            return addFailed(qa, err, prev, null);
          }).finally(function () {
            qa.removeAttribute('aria-busy');
          });
          return;
        }
        var line = e.target.closest('[data-line-key]');
        if (!line) return;
        if (e.target.closest('[data-line-remove]')) {
          if (line.classList.contains('is-removing')) return;  // already on its way out
          clearTimeout(line._t);
          capped.delete(line.dataset.lineKey);
          line.classList.add('is-removing');  // the whole line dims at once (phones); the re-render drops it
          line.querySelector('.cart-qty').setAttribute('aria-busy', 'true');
          changeLine(line.dataset.lineKey, 0);
        } else if (e.target.closest('[data-qty-change]')) {
          var step = e.target.closest('[data-qty-change]');
          if (step.getAttribute('aria-disabled') === 'true' || line.classList.contains('is-removing')) return;  // '+' capped at the stock count
          var dir = parseInt(step.dataset.qtyChange, 10);
          if (dir < 0 && capped.delete(line.dataset.lineKey)) {
            var plus = line.querySelector('[data-qty-change="1"]');
            if (plus) plus.removeAttribute('aria-disabled');
          }
          // quick taps add up: the number follows every tap, one request goes out once tapping pauses
          var span = line.querySelector('.cart-qty span');
          var cur = line.dataset.pendingQty ? parseInt(line.dataset.pendingQty, 10) : parseInt(span.textContent, 10) || 0;
          var next = Math.max(0, cur + dir);
          line.dataset.pendingQty = next;
          span.textContent = next;
          line.querySelector('.cart-qty').setAttribute('aria-busy', 'true');
          clearTimeout(line._t);
          line._t = setTimeout(function () { changeLine(line.dataset.lineKey, next); }, 250);
        }
      });
    }

    /* AJAX product forms */
    document.querySelectorAll('form[data-ajax-cart]').forEach(function (form) {
      // a typed quantity above stock goes to Shopify, which caps it and says why (shown in the drawer),
      // instead of the browser's validation bubble
      form.noValidate = true;
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (form.dataset.busy === 'true') return;  // e.g. a second tap on the sticky bar while adding
        var btn = form.querySelector('[type="submit"]');
        var idInput = form.querySelector('[name="id"]');
        if (!idInput || !idInput.value) return;
        var qtyInput = form.querySelector('[name="quantity"]');
        var items = [{ id: parseInt(idInput.value, 10), quantity: qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1 }];
        form.querySelectorAll('[data-addon-id]').forEach(function (addon) {
          if (addon.checked) items.push({ id: parseInt(addon.dataset.addonId, 10), quantity: 1 });
        });
        var prev = cartCount();
        var html = btn ? btn.innerHTML : '';
        form.dataset.busy = 'true';
        clearInlineStatus(btn);
        if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); btn.textContent = str('adding', 'adding…'); }
        addItems(items).then(function () {
          // phones: the buy box starts over once its items are in the cart, so the sticky bar can't
          // re-add the add-on. The events drive the row, total and label sync. Desktop keeps the form as it is.
          try {
            if (phoneMq && !phoneMq.matches) return;
            var q = form.querySelector('[name="quantity"]');
            if (q && q.value !== '1') { q.value = 1; q.dispatchEvent(new Event('input', { bubbles: true })); }
            form.querySelectorAll('[data-addon-id]:checked').forEach(function (a) {
              a.checked = false;
              a.dispatchEvent(new Event('change', { bubbles: true }));
            });
          } catch (e2) { /* the add itself went through */ }
        }).catch(function (err) {
          return addFailed(btn || form, err, prev, null);
        }).finally(function () {
          delete form.dataset.busy;
          if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); btn.innerHTML = html; }
        });
      });
    });

    /* quick-add triggers (product cards, bundles, and every "order / buy now" CTA).
       Works on <button> and <a>: we preventDefault so a CTA adds to cart and opens the
       drawer instead of navigating to the product page. If the add fails (or JS-less),
       an <a> still falls back to its href so the shopper is never stranded. */
    document.querySelectorAll('[data-quick-add]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        var ids = btn.dataset.quickAdd.split(',').map(function (s) { return parseInt(s.trim(), 10); }).filter(Boolean);
        if (!ids.length) return;
        e.preventDefault();
        if (btn.getAttribute('aria-busy') === 'true') return; // no second unit on double click
        // from the menu CTA: the drawer that opens next takes over the menu's history entry
        if (menuApi && menuApi.isOpen()) menuApi.close({ fromPop: true, keepFocus: true });
        var isLink = btn.tagName === 'A';
        var href = btn.getAttribute('href');
        var html = btn.innerHTML;
        var prev = cartCount();
        clearInlineStatus(btn);
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        if (!isLink) { var t = btn.querySelector('span') || btn; t.textContent = str('adding', 'adding…'); }
        addItems(ids.map(function (id) { return { id: id, quantity: 1 }; })).catch(function (err) {
          return addFailed(btn, err, prev, isLink && href && href !== '#' ? href : null);
        }).finally(function () {
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
          if (!isLink) btn.innerHTML = html;
        });
      });
    });

    fetchCart().then(updateCartUi).catch(function () { /* keep the server-rendered count */ });
  }

  /* ---------- product page ---------- */
  function initProductForms() {
    document.querySelectorAll('[data-product-root]').forEach(function (root) {
      var dataEl = root.querySelector('[data-product-json]');
      if (!dataEl) return;
      var product;
      try { product = JSON.parse(dataEl.textContent); } catch (e) { return; }
      var form = root.querySelector('form[data-ajax-cart]');
      var idInput = form && form.querySelector('[name="id"]');
      var buyBtn = form && form.querySelector('[type="submit"]');
      var buyHtml = buyBtn && !buyBtn.disabled ? buyBtn.innerHTML : null;  // the button as Liquid rendered it

      function currentVariant() {
        var selected = [];
        root.querySelectorAll('[data-option-index]').forEach(function (group) {
          var input = group.querySelector('input:checked, select');
          selected[parseInt(group.dataset.optionIndex, 10)] = input ? input.value : null;
        });
        return product.variants.find(function (v) {
          return v.options.every(function (opt, i) { return selected[i] === null || opt === selected[i]; });
        });
      }

      function refreshTotal() {
        var totalEl = root.querySelector('[data-product-total]');
        if (!totalEl || !idInput) return;
        var variant = product.variants.find(function (v) { return String(v.id) === idInput.value; });
        if (!variant) return;
        var qty = 1;
        var qtyInput = form.querySelector('[name="quantity"]');
        if (qtyInput) qty = parseInt(qtyInput.value, 10) || 1;
        var total = variant.price * qty;
        root.querySelectorAll('[data-addon-id]:checked').forEach(function (a) {
          total += parseInt(a.dataset.addonPrice || '0', 10);
        });
        totalEl.textContent = formatMoney(total);
      }

      function onVariantChange() {
        var variant = currentVariant();
        var priceEl = root.querySelector('[data-product-price]');
        var compareEl = root.querySelector('[data-product-compare]');
        if (variant) {
          if (idInput) idInput.value = variant.id;
          if (priceEl) priceEl.textContent = formatMoney(variant.price);
          if (compareEl) {
            compareEl.style.display = variant.compare_at_price > variant.price ? '' : 'none';
            if (variant.compare_at_price) compareEl.textContent = formatMoney(variant.compare_at_price);
          }
          // only rewrite the label when availability flips, so the rendered markup stays as it is
          if (buyBtn && buyBtn.disabled === !!variant.available) {
            buyBtn.disabled = !variant.available;
            if (!variant.available) buyBtn.textContent = str('soldOut', 'sold out');
            else if (buyHtml) buyBtn.innerHTML = buyHtml;
            else buyBtn.textContent = buyBtn.dataset.addLabel || str('addToCart', 'add to cart');
          }
        }
        // cards with an input follow it; linked cards (no input) keep the state Liquid rendered
        root.querySelectorAll('.variant-card').forEach(function (card) {
          var inp = card.querySelector('input');
          if (inp) card.classList.toggle('is-selected', inp.checked);
        });
        refreshTotal();
      }

      root.querySelectorAll('[data-option-index] input, [data-option-index] select').forEach(function (input) {
        input.addEventListener('change', onVariantChange);
      });
      root.querySelectorAll('[data-addon-id]').forEach(function (addon) {
        addon.addEventListener('change', function () {
          var row = addon.closest('.addon-row');
          if (row) row.classList.toggle('is-checked', addon.checked);
          refreshTotal();
        });
      });

      /* gallery */
      var mainImg = root.querySelector('[data-gallery-main] img');
      root.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          if (mainImg && thumb.dataset.src) {
            mainImg.removeAttribute('srcset');  /* let the swapped src win over the initial responsive srcset */
            mainImg.removeAttribute('sizes');
            mainImg.src = thumb.dataset.src;
            mainImg.alt = thumb.dataset.alt || '';
          }
          root.querySelectorAll('[data-gallery-thumb]').forEach(function (t) { t.classList.remove('is-active'); });
          thumb.classList.add('is-active');
        });
      });

      /* quantity (capped at the input's max, i.e. what is in stock) */
      var qtyField = form && form.querySelector('[name="quantity"]');
      var paintQty = function () {
        if (!qtyField) return;
        var max = parseInt(qtyField.max, 10) || Infinity;
        var v = parseInt(qtyField.value, 10) || 1;
        root.querySelectorAll('[data-qty-btn="1"]').forEach(function (b) { b.disabled = v >= max; });
      };
      root.querySelectorAll('[data-qty-btn]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var input = form.querySelector('[name="quantity"]');
          if (!input) return;
          var max = parseInt(input.max, 10) || Infinity;
          input.value = Math.min(max, Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(btn.dataset.qtyBtn, 10)));
          paintQty();
          refreshTotal();
        });
      });
      if (qtyField) qtyField.addEventListener('input', function () { paintQty(); refreshTotal(); });
      paintQty();

      /* the browser can restore ticked add-ons and a typed quantity after init (Back, back/forward cache)
         without any change event: repaint the rows, the stepper and the total from the real form state */
      function syncForm() {
        root.querySelectorAll('[data-addon-id]').forEach(function (a) {
          var row = a.closest('.addon-row');
          if (row) row.classList.toggle('is-checked', a.checked);
        });
        paintQty();
        refreshTotal();
      }

      onVariantChange();
      syncForm();
      window.addEventListener('pageshow', syncForm);
    });
  }

  /* ---------- cart page: quantity and remove over AJAX ---------- */
  var cartPageSync = null;  // afk:cart listener of the current cart page (the theme editor can re-render the section)

  function initCartPage() {
    var page = document.querySelector('[data-cart-page]');
    if (!page || page.dataset.cartInit) return;
    page.dataset.cartInit = '1';
    page.classList.add('is-ajax');  // also set inline in main-cart.liquid before first paint; "update cart" is the no-JS path (hidden on phones)
    var busy = false, refreshing = null, refreshQueued = false;
    // quick taps add up, as in the drawer: the number follows every tap, and one request per line goes out once
    // tapping pauses (or once the request in flight is back). Keyed by line item key and kept outside the DOM,
    // because every re-render replaces the lines (and a removal shifts the line numbers).
    var pending = {}, queued = {}, timers = {};

    function lineEl(key) { return page.querySelector('[data-line-key="' + key + '"]'); }

    // a quantity that is still on its way: shown right after the tap, and again after each re-render
    function paintPending(key) {
      var line = lineEl(key);
      var input = line && line.querySelector('.cart-qty input');
      if (!input || pending[key] == null) return;
      input.value = pending[key];
      line.querySelector('.cart-qty').setAttribute('aria-busy', 'true');
      var plus = line.querySelector('[data-qty-change="1"]');
      if (plus) {
        plus.disabled = pending[key] >= (parseInt(input.max, 10) || Infinity);  // stock cap, as rendered by Liquid
        if (plus.disabled && document.activeElement === plus) focusSafely(input);
      }
    }

    function send(key) {
      clearTimeout(timers[key]);
      delete timers[key];
      if (pending[key] == null) return;
      if (busy) { queued[key] = true; return; }
      change(key, pending[key]);
    }

    function applySection(html) {
      var fresh = new DOMParser().parseFromString(html || '', 'text/html').querySelector('[data-cart-page]');
      if (!fresh) return;
      // the live wallet row (phones) moves into the new markup as it is, so its buttons never re-initialise
      var oldW = page.querySelector('[data-cart-wallets]');
      // keep keyboard focus on the same control of the same line across the re-render
      var act = document.activeElement, fLine = act && page.contains(act) ? act.closest('[data-line-key]') : null;
      var fKey = null, fIdx = 0, fSel = null;
      if (fLine) {
        fKey = fLine.dataset.lineKey;
        fIdx = parseInt(fLine.dataset.lineIndex, 10) || 0;
        fSel = act.hasAttribute('data-qty-change') ? '[data-qty-change="' + act.getAttribute('data-qty-change') + '"]'
          : act.hasAttribute('data-line-remove') ? '[data-line-remove]'
          : act.matches('.cart-qty input') ? '.cart-qty input' : null;
      }
      page.innerHTML = fresh.innerHTML;
      var tpl = page.querySelector('[data-cart-wallets-host] > template[data-wallets-later]');
      if (tpl && phoneMq && phoneMq.matches) tpl.parentNode.replaceChild(oldW || document.importNode(tpl.content, true), tpl);
      Object.keys(pending).forEach(paintPending);
      if (fSel) {
        var line = lineEl(fKey), target = line && line.querySelector(fSel);
        if (target && target.disabled) target = line.querySelector('.cart-qty input');  // '+' just reached the stock count
        // the line is gone (removed): the remove button of the line above, else the first one, else the title
        if (!line) target = page.querySelector('[data-line-index="' + (fIdx - 1) + '"] [data-line-remove]') || page.querySelector('[data-line-remove]') || page.querySelector('.cart-page__title') || page.querySelector('.cart-empty__actions .btn-primary');
        focusSafely(target);
      }
      page.setAttribute('data-page-total', fresh.getAttribute('data-page-total'));
      page.setAttribute('data-page-count', fresh.getAttribute('data-page-count'));
      if (cartState) renderShippingBars(cartState);
    }

    function refreshCartSection(attempt) {
      var id = page.dataset.sectionId, again = false;
      attempt = attempt || 0;
      if (!id) return Promise.resolve();
      if (refreshing) { refreshQueued = true; return refreshing; }
      refreshing = fetch(location.pathname + '?sections=' + encodeURIComponent(id)).then(function (r) { return r.json(); }).then(function (s) {
        if (s && typeof s[id] === 'string') applySection(s[id]);
        else again = attempt < 2;  // the section came back empty (null): ask twice more before leaving the page as it is
      }).catch(function () { /* the page stays as it is */ }).then(function () {
        refreshing = null;
        if (again) return new Promise(function (done) { setTimeout(done, attempt ? 2000 : 800); }).then(function () { return refreshCartSection(attempt + 1); });
        if (refreshQueued) { refreshQueued = false; return refreshCartSection(); }
      });
      return refreshing;
    }

    function change(key, qty) {
      busy = true;
      var id = page.dataset.sectionId;
      [].forEach.call(page.querySelectorAll('[data-add-status]'), function (s) { s.parentNode.removeChild(s); });
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty, sections: [WALLETS, id].filter(Boolean).join(','), sections_url: location.pathname })
      }).then(function (r) {
        // an HTTP error (e.g. more than in stock) carries Shopify's message; a rate-limit page may not be JSON
        if (!r.ok) return r.json().catch(function () { return {}; }).then(function (err) { throw err; });
        return r.json();
      }).then(function (cart) {
        if (pending[key] === qty) delete pending[key];  // newer taps stay pending and go out next
        var sec = cart.sections || {};
        updateCartUi(cart);  // the afk:cart listener below skips while busy
        renderWallets(sec[WALLETS]);
        if (id && sec[id]) applySection(sec[id]); else return refreshCartSection();
      }).catch(function (err) {
        // no navigation (GET /cart/change over stock is a blank 422 page): re-render the real cart, then say why under the line
        if (pending[key] === qty) delete pending[key];
        var msg = (err && err.description) || str('cartUpdateError', 'could not update the cart.');
        return fetchCart().then(updateCartUi, function () { /* keep the counts */ }).then(refreshCartSection).then(function () {
          var line = lineEl(key), input = line && line.querySelector('.cart-qty input');
          if (input && pending[key] == null) {
            // still the old markup if the refresh failed too: back to the number the server rendered
            input.value = input.defaultValue;
            line.querySelector('.cart-qty').removeAttribute('aria-busy');
            var plus = line.querySelector('[data-qty-change="1"]');
            if (plus) plus.disabled = (parseInt(input.defaultValue, 10) || 0) >= (parseInt(input.max, 10) || Infinity);
          }
          inlineStatus((line && line.querySelector('.cart-line__controls')) || page.querySelector('[name="checkout"]'), msg);
        });
      }).then(settle, settle);
    }

    // after each request: the next line that waited for it
    function settle() {
      busy = false;
      var keys = Object.keys(queued);
      for (var i = 0; i < keys.length && !busy; i++) { delete queued[keys[i]]; send(keys[i]); }
    }

    page.addEventListener('click', function (e) {
      var line = e.target.closest('[data-line-key]');
      if (!line) return;
      var key = line.dataset.lineKey;
      if (pending[key] === 0) return;  // already being removed
      if (e.target.closest('[data-line-remove]')) {
        pending[key] = 0;
        paintPending(key);
        send(key);  // at once (or right after the request in flight)
      } else if (e.target.closest('[data-qty-change]')) {
        var btn = e.target.closest('[data-qty-change]');
        if (btn.disabled) return;  // '+' at the stock count
        var input = line.querySelector('.cart-qty input');
        var cur = pending[key] != null ? pending[key] : (parseInt(input.value, 10) || 1);
        pending[key] = Math.max(0, Math.min(parseInt(input.max, 10) || Infinity, cur + parseInt(btn.dataset.qtyChange, 10)));
        paintPending(key);
        clearTimeout(timers[key]);
        timers[key] = setTimeout(function () { send(key); }, 250);
      }
    });
    page.addEventListener('change', function (e) {
      if (!e.target.matches('.cart-qty input')) return;
      var line = e.target.closest('[data-line-key]');
      if (!line || pending[line.dataset.lineKey] === 0) return;
      // a typed quantity goes out as typed: above stock, Shopify caps it and its message shows under the line
      pending[line.dataset.lineKey] = Math.max(0, parseInt(e.target.value, 10) || 0);
      paintPending(line.dataset.lineKey);
      send(line.dataset.lineKey);
    });
    // Enter in a quantity field: the change handler does the work instead of a full "update cart" post
    page.addEventListener('submit', function (e) {
      var a = document.activeElement;
      if (e.submitter && e.submitter.name === 'checkout') return;
      if (a && a.matches && a.matches('.cart-qty input')) { e.preventDefault(); a.blur(); }
    });
    // changes made elsewhere (drawer, cross-sell adds): re-render. One listener, also after an editor re-render
    if (cartPageSync) document.removeEventListener('afk:cart', cartPageSync);
    cartPageSync = function (e) {
      var c = e.detail || {};
      if (busy) return;
      if (c.total_price !== parseInt(page.dataset.pageTotal, 10) || c.item_count !== parseInt(page.dataset.pageCount, 10)) refreshCartSection();
    };
    document.addEventListener('afk:cart', cartPageSync);
  }

  /* ---------- external links open in a new tab ---------- */
  function initExternalLinks(scope) {
    var root = scope || document;
    var here = window.location.hostname;
    var links = root.querySelectorAll('a[href]');
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      if (a.target) continue;                                            // respect an explicit target
      if (a.protocol !== 'http:' && a.protocol !== 'https:') continue;   // skip mailto:, tel:, #anchors, etc.
      if (!a.hostname || a.hostname === here) continue;                  // skip internal links
      a.target = '_blank';
      var rel = (a.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
      if (rel.indexOf('noopener') === -1) rel.push('noopener');
      if (rel.indexOf('noreferrer') === -1) rel.push('noreferrer');
      a.setAttribute('rel', rel.join(' '));
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    // Cart first, then everything else — and each init is isolated so a single broken
    // section can never take down add-to-cart, the drawer, or the cart icon.
    [initCart, initProductForms, initCartPage, initOverlays,
     initThemeToggle, initHeader, initAnnounce, initMobileMenu, initLedBars,
     initConfigurators, initMobileBuyBar, initTypingState, initFaqGroups, initExternalLinks].forEach(function (fn) {
      try { fn(); } catch (err) { if (window.console && console.error) console.error('[theme init] ' + (fn.name || 'init') + ' failed:', err); }
    });
  });

  /* re-init dynamic bits when the theme editor re-renders a section */
  document.addEventListener('shopify:section:load', function (e) {
    initLedBars();
    initConfigurators();
    initFaqGroups();
    initThemeToggle();
    initCartPage();  // a re-rendered main-cart needs its handlers again
    initExternalLinks(e && e.target ? e.target : document);
  });
})();
